import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Truck, Plus, Trash2, Lock, ArrowLeft, Pencil, Save } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { NurseryDeliveryZone, NurseryCustomerTier, CreateDeliveryZonePayload } from '../../types/nursery';

// Labels and hints: match.<value>.label / .hint in the DeliveryZonesList locale file.
const MATCH_TYPES: { value: NurseryDeliveryZone['match_type'] }[] = [
  { value: 'all' },
  { value: 'postcode_prefix' },
  { value: 'postcode_list' },
];

const EMPTY_FORM: CreateDeliveryZonePayload = {
  name: '',
  match_type: 'all',
  match_value: '',
  flat_fee: 0,
  free_over_subtotal: null,
  customer_tier_id: null,
};

function money(n: number | null): string {
  if (n == null) return '—';
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

function DeliveryZonesList() {
  const navigate = useNavigate();
  const { t } = useTranslation('nursery', { keyPrefix: 'DeliveryZonesList' });
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [zones, setZones] = useState<NurseryDeliveryZone[]>([]);
  const [tiers, setTiers] = useState<NurseryCustomerTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<CreateDeliveryZonePayload>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  /** The zone the form is editing; null when it is adding a new one. */
  const [editingId, setEditingId] = useState<number | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [zoneRes, tierRes] = await Promise.all([
        nurseryService.getDeliveryZones(true),
        nurseryService.getCustomerTiers(),
      ]);
      if (zoneRes.success) setZones(zoneRes.data || []);
      if (tierRes.success) setTiers(tierRes.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (z: NurseryDeliveryZone) => {
    setEditingId(z.id);
    setForm({
      name: z.name,
      match_type: z.match_type,
      match_value: z.match_value ?? '',
      flat_fee: Number(z.flat_fee),
      free_over_subtotal: z.free_over_subtotal == null ? null : Number(z.free_over_subtotal),
      customer_tier_id: z.customer_tier_id ?? null,
    });
    setError(null);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const saveZone = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setError(null);
    const payload = {
      ...form,
      name: form.name.trim(),
      match_value: form.match_type === 'all' ? null : (form.match_value || '').trim() || null,
      free_over_subtotal: form.free_over_subtotal ?? null,
    };
    try {
      const res = editingId != null
        ? await nurseryService.updateDeliveryZone(editingId, payload)
        : await nurseryService.createDeliveryZone(payload);
      if (res.success) {
        setForm(EMPTY_FORM);
        setEditingId(null);
        await load();
      } else {
        setError((res as { error?: { message?: string } }).error?.message || t('saveFailed'));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const removeZone = async (id: number) => {
    await nurseryService.deleteDeliveryZone(id);
    setZones((z) => z.filter((x) => x.id !== id));
    if (editingId === id) cancelEdit();
  };

  const toggleActive = async (zone: NurseryDeliveryZone) => {
    const res = await nurseryService.updateDeliveryZone(zone.id, { active: !zone.active });
    // The update returns the bare row, without the tier's name.
    if (res.success) setZones((z) => z.map((x) => (x.id === zone.id ? { ...x, active: res.data.active } : x)));
  };

  if (licenseLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">{t('licenceTitle')}</h2>
          <p className="text-sm text-neutral-500 mb-4">
            <Trans t={t} i18nKey="licenceBody" components={{ strong: <span className="font-medium" /> }} />
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('backToNursery')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<Truck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">{error}</div>
        )}

        {/* Add form */}
        <div ref={formRef} className={`bg-white border rounded-xl p-5 mb-6 scroll-mt-24 ${editingId != null ? 'border-primary-300' : 'border-neutral-200'}`}>
          <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">
            {editingId != null ? t('editZone') : t('newZone')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">{t('name')}</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t('namePlaceholder')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">{t('appliesToTier')}</label>
              <select
                value={form.customer_tier_id ?? ''}
                onChange={(e) => setForm({ ...form, customer_tier_id: e.target.value === '' ? null : Number(e.target.value) })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{t('allTiers')}</option>
                {tiers.map((tier) => <option key={tier.id} value={tier.id}>{tier.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">{t('match.label')}</label>
              <select
                value={form.match_type}
                onChange={(e) => setForm({ ...form, match_type: e.target.value as NurseryDeliveryZone['match_type'] })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {MATCH_TYPES.map((m) => <option key={m.value} value={m.value}>{t(`match.${m.value}.label`)}</option>)}
              </select>
            </div>
            {form.match_type !== 'all' && (
              <div className="lg:col-span-3">
                <label className="block text-xs font-medium text-neutral-500 mb-1">
                  {form.match_type === 'postcode_prefix' ? t('postcodesPrefixes') : t('postcodesExact')}
                </label>
                <input
                  value={form.match_value ?? ''}
                  onChange={(e) => setForm({ ...form, match_value: e.target.value })}
                  placeholder={t(`match.${form.match_type}.hint`)}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">{t('flatFee')}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.flat_fee ?? 0}
                onChange={(e) => setForm({ ...form, flat_fee: Number(e.target.value) })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">{t('freeOver')}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.free_over_subtotal ?? ''}
                onChange={(e) => setForm({ ...form, free_over_subtotal: e.target.value === '' ? null : Number(e.target.value) })}
                placeholder={t('neverFree')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex flex-wrap items-end gap-2">
              {editingId != null ? (
                <>
                  <Button variant="primary" size="sm" onClick={saveZone} disabled={saving || !form.name.trim()}>
                    <Save className="h-4 w-4" /> {saving ? t('saving') : t('saveZone')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={cancelEdit} disabled={saving}>{t('cancel')}</Button>
                </>
              ) : (
                <Button variant="primary" size="sm" onClick={saveZone} disabled={saving || !form.name.trim()}>
                  <Plus className="h-4 w-4" /> {t('addZone')}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Zones list */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-neutral-100 rounded-lg animate-pulse" />)}
          </div>
        ) : zones.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <Truck className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">{t('emptyTitle')}</p>
            <p className="text-sm mt-1">{t('emptyBody')}</p>
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
            {zones.map((z) => (
              <div key={z.id} className={`flex items-center gap-3 px-4 py-3 ${z.active ? '' : 'opacity-50'} ${editingId === z.id ? 'bg-primary-50/50' : ''}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-neutral-800 break-words min-w-0">{z.name}</span>
                    {z.tier_name && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {z.tier_name}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 mt-0.5 break-words">
                    {z.match_type === 'all' ? t('allPostcodes') : z.match_type === 'postcode_prefix' ? t('prefixValue', { value: z.match_value }) : t('listValue', { value: z.match_value })}
                    {' · '}{t('feeExGst', { amount: money(Number(z.flat_fee)) })}
                    {z.free_over_subtotal != null ? ` · ${t('freeOverAmount', { amount: money(Number(z.free_over_subtotal)) })}` : ''}
                  </p>
                </div>
                <button onClick={() => startEdit(z)} className="text-neutral-400 hover:text-primary-600 p-1" title={t('edit')} aria-label={t('editNamed', { name: z.name })}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => toggleActive(z)} className="text-xs text-neutral-500 hover:text-primary-600">
                  {z.active ? t('disable') : t('enable')}
                </button>
                <button onClick={() => removeZone(z.id)} className="text-neutral-400 hover:text-red-600 p-1" title={t('delete')} aria-label={t('deleteNamed', { name: z.name })}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default DeliveryZonesList;
